import { getPublicRuntimeConfig } from '~/cynqueModules/nuxtAccessors'
import type { IEntityDataItem, TEntityId } from '~/types/models'

/**
 * Delete-refusal enforcement for the mock API.
 *
 * ⚠ THE MOCK COULD NOT REFUSE A DELETE, AND THAT HID A WHOLE CLASS.
 * `deleteEntityListData` / `deleteEntityListDataMultiple` removed whatever they
 * were given and returned 204, unconditionally — even though the fixtures have
 * carried `canBeDeleted: false` rows the entire time. So no unit test, no e2e
 * run and no dev session could produce a 409 on DELETE, and the first sighting
 * was a production fault (Honeybadger 134111024 / 134110338, 2026-09-04) in
 * which a user retried a blocked delete three times in 26 minutes.
 *
 * The real API refuses from the live FK graph: the entity views compute
 *   can_be_deleted = not row_delete_blocked(<table>::regclass, <pk>::text)
 * which walks the delete closure (ON DELETE CASCADE edges plus declared
 * propagations) and then looks for referrers OUTSIDE it. The mock cannot model
 * that, and does not try — it reads the `canBeDeleted` the fixtures already
 * carry. That is an approximation of the mechanism but an exact match of the
 * OBSERVABLE: the client's only input is the flag on the row it was handed.
 *
 * ⚠ LENIENT, like `isStaleVersion`: a row with no `canBeDeleted` at all is
 * deletable. Mock fixtures are inconsistent about the field, and being strict
 * would reject flows on the strength of a missing key rather than a real
 * constraint.
 */
export function doEnforceMockDeleteConflicts(): boolean {
  if (typeof window === 'undefined') {
    return false
  }
  return getPublicRuntimeConfig().mockEnforceDeleteConflicts !== false
}

/**
 * The 409 body.
 *
 * ⚠ SHAPED TO MATCH THE REAL API ON THIS PATH, unlike `versionConflictBody`
 * whose shape is unknown and therefore chosen to be merely useful. Here it is
 * known: `problem_info_for` maps a `DeleteConflict` carrying an asyncpg
 * `ForeignKeyViolationError` to
 *   ProblemInfo(409, 'DeleteConflict', 'Delete conflict',
 *               f'{entity_name}={id} is referenced by a {exc.table_name} object.')
 * and `describeErrorBody` reads `detail` before anything else. Emitting `detail`
 * is what makes the client's message identical against mock and production —
 * which is the entire point of closing this gap rather than inventing a body.
 */
export function deleteConflictBody({
  entityName,
  id,
  referencedBy,
}: {
  entityName: string
  id: TEntityId | undefined
  referencedBy?: string
}) {
  return {
    statusCode: 409,
    kind: 'DeleteConflict',
    title: 'Delete conflict',
    detail:
      `${entityName}=${id} is referenced by` +
      ` a ${referencedBy ?? 'related'} object.`,
  }
}

/**
 * The first row in `ids` that the stored data says cannot be deleted, or
 * `undefined` when every one of them may go.
 *
 * ⚠ THE GATE IS INSIDE THIS FUNCTION, for the reason spelled out on
 * `findVersionConflict`: a gate a caller can omit is a gate that will be
 * omitted. Both delete entry points call this, and a third one added later gets
 * the check by calling it rather than by remembering to.
 *
 * Returns the row's `index` in `ids` so a multi-id caller can say WHICH id it
 * refused — the real API refuses the whole request, and knowing which member
 * caused it is the difference between a fixable report and a shrug.
 */
export function findDeleteConflict({
  listData,
  ids,
}: {
  listData: IEntityDataItem[]
  ids: TEntityId[]
}): { id: TEntityId; index: number } | undefined {
  if (!doEnforceMockDeleteConflicts()) {
    return undefined
  }
  for (const [index, id] of ids.entries()) {
    const stored = listData.find((item) => item.id === id)
    if (stored && stored.canBeDeleted === false) {
      return { id, index }
    }
  }
  return undefined
}
